import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import { createInternalNotesRepository } from '../conversations/internal-notes-repository'
import { useTenant } from '../tenant/TenantProvider'
import { PatientDetail } from './components/PatientDetail'
import styles from './PatientsPage.module.css'
import {
  createPatientReminderPreferenceRepository,
  type PatientReminderPreferenceRepository,
} from './patient-reminder-preference-repository'
import {
  createPatientsRepository,
  type PatientsRepository,
} from './patients-repository'
import type { PatientAddOutcome } from './patients-model'
import { usePatient } from './use-patients'

/** Add patient's result, handed over once through navigation state. */
function addOutcomeFrom(state: unknown): PatientAddOutcome | undefined {
  const value =
    state && typeof state === 'object'
      ? (state as { patientAddOutcome?: unknown }).patientAddOutcome
      : undefined
  return value === 'MATCH' || value === 'CREATED' ? value : undefined
}

export function PatientDetailContent({
  tenantId,
  patientAuthorityId,
  repository,
  onBack,
  onOpenConversation,
  onAddAppointment,
  internalNotesRepository,
  reminderPreferenceRepository,
  addOutcome,
}: {
  tenantId: string
  patientAuthorityId: string
  addOutcome?: PatientAddOutcome
  repository: PatientsRepository
  onBack: () => void
  onOpenConversation: (aggregateId: string) => void
  onAddAppointment: () => void
  internalNotesRepository?: ReturnType<typeof createInternalNotesRepository>
  reminderPreferenceRepository?: PatientReminderPreferenceRepository
}) {
  const result = usePatient(repository, tenantId, patientAuthorityId)
  if (result.status === 'loading')
    return (
      <div className={styles.detailState}>
        <Spinner label="Loading patient" />
      </div>
    )
  if (result.status === 'forbidden')
    return (
      <div className={styles.detailState}>
        <StatePanel
          kind="forbidden"
          title="Patient unavailable"
          description="You do not have permission to view this clinic's patients."
        />
      </div>
    )
  if (result.status === 'error')
    return (
      <div className={styles.detailState}>
        <StatePanel
          kind="error"
          title="Patient unavailable"
          description="The practice patient record could not be loaded."
        />
      </div>
    )
  const patient = result.patient
  if (!patient)
    return (
      <div className={styles.detailState}>
        <StatePanel
          kind="empty"
          title="Patient not found"
          description="This record may have been merged, removed, or is no longer active in the practice system."
        />
      </div>
    )
  return (
    <PatientDetail
      patient={patient}
      tenantId={tenantId}
      internalNotesRepository={internalNotesRepository}
      reminderPreferenceRepository={reminderPreferenceRepository}
      addOutcome={addOutcome}
      onBack={onBack}
      onOpenConversation={() => {
        if (patient.interactionAggregateId) {
          onOpenConversation(patient.interactionAggregateId)
        }
      }}
      onAddAppointment={onAddAppointment}
    />
  )
}

export function PatientDetailPage({
  repository,
  reminderPreferenceRepository,
}: {
  repository?: PatientsRepository
  reminderPreferenceRepository?: PatientReminderPreferenceRepository
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const { patientAuthorityId = '' } = useParams()
  const [addOutcome] = useState(() => addOutcomeFrom(location.state))
  const handedOver = useRef(false)
  useEffect(() => {
    // Shown once: a reload or a later visit does not repeat it.
    if (!addOutcome || handedOver.current) return
    handedOver.current = true
    void navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    })
  }, [addOutcome, location.pathname, location.search, navigate])
  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const activeRepository = useMemo(
    () =>
      repository ?? createPatientsRepository({ getUser: () => currentUser }),
    [repository, currentUser]
  )
  const notesRepository = useMemo(
    () => createInternalNotesRepository({ getUser: () => currentUser }),
    [currentUser]
  )
  const activeReminderPreferenceRepository = useMemo(
    () =>
      reminderPreferenceRepository ??
      createPatientReminderPreferenceRepository({
        getUser: () => currentUser,
      }),
    [reminderPreferenceRepository, currentUser]
  )
  if (!tenant.selectedClinic) return null
  return (
    <PatientDetailContent
      key={`${tenant.selectedClinic.id}:${patientAuthorityId}`}
      tenantId={tenant.selectedClinic.id}
      patientAuthorityId={patientAuthorityId}
      repository={activeRepository}
      internalNotesRepository={notesRepository}
      reminderPreferenceRepository={activeReminderPreferenceRepository}
      addOutcome={addOutcome}
      onBack={() => void navigate('/patients')}
      onOpenConversation={(aggregateId) =>
        void navigate(
          `/conversations?aggregate=${encodeURIComponent(aggregateId)}`
        )
      }
      onAddAppointment={() =>
        void navigate(
          `/appointments?patient=${encodeURIComponent(patientAuthorityId)}`
        )
      }
    />
  )
}
